import type { FreeTextInputMode, FreeTextProperties } from '../../../api/types';
import type { RouteMaster } from '../../../data/routes';
import type { RouteStop } from '../../../data/routeStops';
import { BroadcastConditionsEditor } from '../conditions/BroadcastConditionsEditor';
import { FREE_TEXT_MODE_META, FREE_TEXT_MODE_TESTID } from '../constants';
import { useAudioPreview } from '../hooks/useAudioPreview';
import { useAudioUpload } from '../hooks/useAudioUpload';
import { resolveInputMode } from '../logic';
import type { PreviewSsmlHandler } from '../types';

export function FreeTextFields({
  props,
  routes,
  routeStops,
  parentVoiceId,
  playbackRate,
  onChange,
  onUploadAudio,
  onPreviewSsml,
}: {
  props: Partial<FreeTextProperties>;
  routes: RouteMaster[];
  routeStops: Record<string, RouteStop[]>;
  /**
   * 親フォームで選択中の Polly VoiceId。プレビュー合成 (POST /audio-preview) に
   * `voiceId` として渡すことで、実放送と同じ声色で試聴できるようにする。
   * 空文字 / 未指定なら voiceId を送らず (= サーバー側デフォルト Takumi)。
   */
  parentVoiceId?: string;
  /**
   * 親コンテンツの `playback_rate` (要件 2)。
   * <audio> のプレビュー再生時に `playbackRate` として反映する。
   * `undefined` / `1` は等倍再生。範囲 (0.5〜2.0) 外の値は 1.0 に落として無視する。
   */
  playbackRate?: number;
  onChange: (props: FreeTextProperties) => void;
  onUploadAudio?: (file: File) => Promise<string>;
  onPreviewSsml?: PreviewSsmlHandler;
}): React.JSX.Element {
  const mode = resolveInputMode(props);
  const plainText = props.text ?? '';
  const ssmlText = props.ssml ?? '';

  // 音声プレビューの副作用 (生成中フラグ / エラー / object URL / 再生速度反映) は
  // `useAudioPreview` に切り出している。要件 2 の playback_rate 反映も同フック内で行う。
  const {
    previewing,
    previewError,
    previewUrl,
    previewAudioRef,
    effectivePlaybackRate,
    startPreview,
  } = useAudioPreview({ onPreviewSsml, playbackRate });

  // 音声ファイルアップロードの副作用 (アップロード中フラグ / エラー) は
  // `useAudioUpload` に切り出している。
  const { uploading, uploadError, handleFileChange } = useAudioUpload({
    onUploadAudio,
    onUploaded: (audioKey) => onChange({ ...props, input_mode: 'audio', audio_key: audioKey }),
  });

  // プレビュー対象 (audio モードはプレビュー不可)
  const previewableText = mode === 'ssml' ? ssmlText : mode === 'text' ? plainText : '';
  const canPreview = mode !== 'audio' && previewableText.trim() !== '';

  const handleModeChange = (next: FreeTextInputMode): void => {
    onChange({ ...props, input_mode: next });
  };

  const handlePreview = async (): Promise<void> => {
    if (!onPreviewSsml || !canPreview) return;
    // 親フォームで選択中の Polly VoiceId (空文字なら undefined 化) を
    // プレビュー入力に含める。実放送と同じ声色で試聴するため。
    const voiceId =
      typeof parentVoiceId === 'string' && parentVoiceId !== '' ? parentVoiceId : undefined;
    const baseInput = mode === 'ssml' ? { ssml: ssmlText } : { text: plainText, isSsml: false };
    const input = voiceId ? { ...baseInput, voiceId } : baseInput;
    await startPreview(input);
  };

  const handleRemoveAudio = (): void => {
    const next = { ...props };
    delete next.audio_key;
    onChange(next);
  };

  return (
    <div className="contents-fields">
      {/*
        放送条件セクション (任意テキスト放送の先頭に配置)。
        任意テキスト放送はデフォルトでは無条件で放送されるが、ここで放送条件 (接続線遅延) を
        複数追加することで「〜のとき限定放送」に絞り込める (AND 判定)。
        UI・データモデルは接続線遅延コンテンツ (ConnectingLineDelayFields) を踏襲する。

        コンポーネント実装は別ファイル `./BroadcastConditionsEditor.tsx` を参照。
      */}
      <BroadcastConditionsEditor
        conditions={Array.isArray(props.conditions) ? props.conditions : []}
        routes={routes}
        routeStops={routeStops}
        onChange={(next) => {
          const nextProps: FreeTextProperties = { ...props };
          if (next.length === 0) {
            delete nextProps.conditions;
          } else {
            nextProps.conditions = next;
          }
          onChange(nextProps);
        }}
      />

      <fieldset className="free-text-mode-switch" style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend style={{ padding: 0 }}>入力方法</legend>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {FREE_TEXT_MODE_META.map((m) => (
            <label key={m.mode} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <input
                type="radio"
                /*
                 * E2E / AI エージェントが「通常テキスト」「SSML」「音声ファイル」の
                 * ラジオボタンを安定して特定できるよう data-testid を付与する。
                 *
                 * 画面ラベルは日本語 ("通常テキスト" など) だが value は
                 * FreeTextInputMode キー ("text" / "ssml" / "audio") であり、
                 * "plain" のような別名ではない。AI が画面ラベルから推測した
                 * セレクタ (例: input[value='plain']) を投入して 3 連続失敗で
                 * ステップが中止される事故が報告されたため、testid を付ける。
                 */
                data-testid={FREE_TEXT_MODE_TESTID[m.mode]}
                aria-label={`入力方法: ${m.label}`}
                value={m.mode}
                checked={mode === m.mode}
                onChange={() => handleModeChange(m.mode)}
              />
              {m.label}
            </label>
          ))}
        </div>
      </fieldset>

      {mode === 'text' ? (
        <label>
          {/*
           * 画面表示ラベル (<span>) と aria-label は完全一致させること。
           * 不一致だと E2E / AI エージェントが画面ラベルから要素を特定できず、
           * モーダル内の他のテキスト入力欄 (説明欄: data-testid="description-input") に
           * 誤入力してしまう事象が発生する。
           * 併せて data-testid="content-free-text-plain" でも一意に取得できるようにする。
           */}
          <span>放送文面 (通常テキスト)</span>
          <textarea
            rows={4}
            aria-label="放送文面 (通常テキスト)"
            data-testid="content-free-text-plain"
            value={plainText}
            onChange={(e) =>
              onChange({
                ...props,
                input_mode: 'text',
                text: e.target.value,
              })
            }
            placeholder="まもなく終点です"
          />
        </label>
      ) : null}

      {mode === 'ssml' ? (
        <label>
          {/*
           * 画面表示ラベル (<span>) と aria-label は完全一致 ("放送文面 SSML")。
           * 併せて data-testid="content-free-text-ssml" でも一意に取得できるようにする。
           */}
          <span>放送文面 SSML</span>
          <textarea
            rows={4}
            aria-label="放送文面 SSML"
            data-testid="content-free-text-ssml"
            value={ssmlText}
            onChange={(e) =>
              onChange({
                ...props,
                input_mode: 'ssml',
                ssml: e.target.value,
              })
            }
            placeholder="<speak>まもなく終点です</speak>"
          />
        </label>
      ) : null}

      {mode !== 'audio' && onPreviewSsml ? (
        <div className="ssml-preview-field">
          <button
            type="button"
            onClick={() => void handlePreview()}
            disabled={previewing || !canPreview}
            aria-label="音声プレビュー"
          >
            {previewing ? '生成中...' : '▶ 音声プレビュー'}
          </button>
          {previewUrl ? (
            <audio
              ref={previewAudioRef}
              controls
              src={previewUrl}
              data-testid="ssml-preview-audio"
              style={{ display: 'block', marginTop: '4px' }}
              onLoadedMetadata={(e) => {
                // 要件 2: メタデータ読み込み後にも再生速度を適用する
                // (src 変更・再生開始などのタイミングでリセットされることがあるため、
                // useEffect と onLoadedMetadata の両方で反映する)。
                (e.currentTarget as HTMLAudioElement).playbackRate = effectivePlaybackRate;
              }}
            >
              <track kind="captions" />
            </audio>
          ) : null}
          {previewError ? (
            <div className="error-message" role="alert">
              {previewError}
            </div>
          ) : null}
        </div>
      ) : null}

      {mode === 'audio' ? (
        <div className="audio-upload-field">
          <span>放送音声ファイル (MP3)</span>
          {props.audio_key ? (
            <div className="audio-upload-status" data-testid="audio-uploaded">
              <span>アップロード済み: {props.audio_key}</span>
              <button type="button" className="danger" onClick={handleRemoveAudio}>
                音声を削除
              </button>
            </div>
          ) : (
            <p className="hint" style={{ margin: '4px 0' }}>
              MP3
              音声ファイルをアップロードしてください。アップロードした音声がそのまま放送されます。
            </p>
          )}
          <input
            type="file"
            accept="audio/mpeg,.mp3"
            aria-label="放送音声ファイル (MP3)"
            disabled={uploading || !onUploadAudio}
            onChange={(e) => void handleFileChange(e)}
          />
          {uploading ? <span className="hint">アップロード中...</span> : null}
          {uploadError ? (
            <div className="error-message" role="alert">
              {uploadError}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
